import { useMemo, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Droplets, Info, Activity } from "lucide-react";
import { HydricPieChart } from "./HydricPieChart";
import { MaterialBreakdownChart } from "./MaterialBreakdownChart";
import { AllocationAnalysisCard } from "./AllocationAnalysisCard";
import { TransportLegsCard } from "./TransportLegsCard";
import { formatNumber } from "@/utils/numberFormatter";
import { useCurrentUserCompany } from "@/hooks/useCurrentUserCompany";
import { useData } from "@/hooks/useData";
import {
  FootprintSummary,
  HydricChartData,
  FootprintDocument,
  FootprintProduct,
  FootprintConsumption,
} from "@/types/results";

interface HydricResultsProps {
  selectedProduct: string;
  selectedYear: string;
  onProductChange: (product: string) => void;
  onYearChange: (year: string) => void;
  isSharedView?: boolean;
  sharedCompanyId?: string;
}

export const HydricResults = ({
  selectedProduct,
  selectedYear,
  onProductChange,
  onYearChange,
  isSharedView = false,
  sharedCompanyId,
}: HydricResultsProps) => {
  const { t } = useLanguage();
  const { company, loading: companyLoading } = useCurrentUserCompany();

  const effectiveCompanyId = isSharedView ? sharedCompanyId : company?.id;



  // Fetch data from the new collections
  const { data: footprintDocuments, loading: footprintsLoading } = useData<FootprintDocument>({
    collectionName: "footprints",
  });

  const { data: footprintProducts, loading: productsLoading } = useData<FootprintProduct>({
    collectionName: "footprint_products",
  });

  const { data: footprintConsumptions, loading: consumptionsLoading } = useData<FootprintConsumption>({
    collectionName: "footprint_consumptions",
  });

  // Filter and organize data for current company
  const companyData = useMemo(() => {
    if (companyLoading && !isSharedView) {
      return { footprints: [], products: [], consumptions: [] };
    }

    if (!effectiveCompanyId) {
      return { footprints: [], products: [], consumptions: [] };
    }

    // Filter footprint documents for the current company
    const companyFootprints = footprintDocuments?.filter((fp) => fp.company_id === effectiveCompanyId) || [];

    // Get all footprint IDs for this company
    const companyFootprintIds = companyFootprints.map((fp) => fp.id);

    // Filter products and consumptions that belong to this company's footprints
    const companyProducts = footprintProducts?.filter((fp) => companyFootprintIds.includes(fp.footprint_id)) || [];
    const companyConsumptions =
      footprintConsumptions?.filter((fc) => companyFootprintIds.includes(fc.footprint_id)) || [];

    return {
      footprints: companyFootprints,
      products: companyProducts,
      consumptions: companyConsumptions,
    };
  }, [effectiveCompanyId, footprintDocuments, footprintProducts, footprintConsumptions, companyLoading, isSharedView]);

  // Create hydric results for the charts and dashboard
  const hydricResults = useMemo(() => {
    if (companyData.footprints.length === 0) return [];

    const results: any[] = [];

    companyData.products.forEach((product) => {
      const footprintDoc = companyData.footprints.find((fp) => fp.id === product.footprint_id);

      if (footprintDoc) {
        // Get consumption breakdowns for this product's footprint
        const relatedConsumptions = companyData.consumptions.filter((c) => c.footprint_id === product.footprint_id);

        // Calculate phase breakdowns based on consumption data
        const materialsHydric = relatedConsumptions
          .filter((c) => c.consumption_type === "material")
          .reduce((sum, c) => sum + (c.hydric_footprint || 0), 0);

        const electricityHydric = relatedConsumptions
          .filter((c) => c.consumption_type === "electricity")
          .reduce((sum, c) => sum + (c.hydric_footprint || 0), 0);

        const fuelsHydric = relatedConsumptions
          .filter((c) => c.consumption_type === "fuels")
          .reduce((sum, c) => sum + (c.hydric_footprint || 0), 0);

        const residueHydric = relatedConsumptions
          .filter((c) => c.consumption_type === "residue")
          .reduce((sum, c) => sum + (c.hydric_footprint || 0), 0);

        const waterHydric = relatedConsumptions
          .filter((c) => c.consumption_type === "water")
          .reduce((sum, c) => sum + (c.hydric_footprint || 0), 0);

        // Calculate actual blue and gray water from consumption data
        const waterConsumptions = relatedConsumptions.filter((c) => c.consumption_type === "water");
        
        // Separate gray water by activity type to avoid incorrect summing
        const extractionGrayWater = waterConsumptions.reduce((sum, c) => {
          if (c.activity_type === "extraction") {
            const grayWater = (c.consumption_details as any)?.ph_agua_cinzenta || 
                             (c.consumption_details as any)?.total_ph_agua_cinzenta || 0;
            return sum + grayWater;
          }
          return sum;
        }, 0);
        
        const transformationGrayWater = waterConsumptions.reduce((sum, c) => {
          if (c.activity_type === "transformation") {
            const grayWater = (c.consumption_details as any)?.ph_agua_cinzenta || 
                             (c.consumption_details as any)?.total_ph_agua_cinzenta || 0;
            return sum + grayWater;
          }
          return sum;
        }, 0);
        
        // Calculate product-specific gray water allocation
        // Gray water can exist for both extraction and transformation products
        // It's calculated based on discharge and sludge data in the water consumption step
        // Use the appropriate gray water based on product activity type
        const productActivityType = product.product_flow || footprintDoc.general_data?.activityType;
        const relevantGrayWater = productActivityType === "transformation" ? 
          transformationGrayWater : extractionGrayWater;
        
        const productGrayWaterAllocation = relevantGrayWater * (product.ratio_by_quantity || 0);

        // Calculate total water footprint for the product (blue + gray)
        const totalWaterHydricForProduct = (product.hydric_footprint_water || waterHydric) * (product.annual_production || 0);

        // Calculate product-specific blue water allocation by subtracting gray water from total water
        const productBlueWaterAllocation = totalWaterHydricForProduct - productGrayWaterAllocation;

        // Create result object for charts and dashboard
        const result = {
          id: product.id,
          year: footprintDoc.year,
          companyId: footprintDoc.company_id,
          nome_produto: product.name || "Unknown Product",
          tipo_produto: product.product_type || "outro",
          uF: product.functional_unit?.quantity || 1,
          U: product.functional_unit?.unit || "m3",
          q: product.annual_production || 0,
          fat_prod: product.product_revenue || 0,
          rd: (() => {
            // Calculate decision ratio properly from PVP values
            const generalData = footprintDoc.general_data;
            const activityType = generalData?.activityType;

            if (activityType === "extraction") {
              const pvpCaro = generalData.pvp_caro_ext || 0;
              const pvpBarato = generalData.pvp_barato_ext || 0;
              return pvpBarato > 0 ? pvpCaro / pvpBarato : 1;
            } else if (activityType === "transformation") {
              const pvpCaro = (generalData as any).pvp_caro_trsf || 0;
              const pvpBarato = (generalData as any).pvp_barato_trsf || 0;
              return pvpBarato > 0 ? pvpCaro / pvpBarato : 1;
            }

            return 1; // Default fallback
          })(),
          r_prod: product.ratio_by_quantity || 0,
          r_fat: product.ratio_by_revenue || 0,
          pc_mats: [],
          ph_mats: [],
          createdAt: product.created_at || product.createdAt,
          updatedAt: product.updated_at || product.updatedAt,
          activityType: product.product_flow || footprintDoc.general_data?.activityType || "extraction",
          ph_prod: (product.hydric_footprint_total || 0) * (product.annual_production || 0), // Annual total instead of per-unit

          // Use actual calculated values from the product instead of arbitrary multipliers
          // For extraction activities, use the actual breakdown values (converted to annual totals)
          ph_ext_materials:
            (product.product_flow || footprintDoc.general_data?.activityType || "extraction") !== "transformation"
              ? (product.hydric_footprint_materials || materialsHydric) * (product.annual_production || 0)
              : undefined,
          ph_ext_ecr:
            (product.product_flow || footprintDoc.general_data?.activityType || "extraction") !== "transformation"
              ? (product.hydric_footprint_electricity || electricityHydric + fuelsHydric + residueHydric) *
                (product.annual_production || 0)
              : undefined,
          ph_ext_blue:
            (product.product_flow || footprintDoc.general_data?.activityType || "extraction") !== "transformation"
              ? productBlueWaterAllocation
              : undefined,
          ph_ext_grey:
            (product.product_flow || footprintDoc.general_data?.activityType || "extraction") !== "transformation"
              ? productGrayWaterAllocation
              : undefined,
          ph_ext:
            (product.product_flow || footprintDoc.general_data?.activityType || "extraction") !== "transformation"
              ? ((product.hydric_footprint_materials || materialsHydric) +
                  (product.hydric_footprint_electricity || electricityHydric + fuelsHydric + residueHydric) +
                  (product.hydric_footprint_water || waterHydric)) *
                (product.annual_production || 0)
              : product.hydric_footprint_extraction !== undefined
                ? product.hydric_footprint_extraction * (product.annual_production || 0)
                : undefined,

          // For transformation activities, use backend calculated values when available (converted to annual totals)
          ph_trans:
            (product.product_flow || footprintDoc.general_data?.activityType || "extraction") !== "extraction"
              ? (product.hydric_footprint_transport || 0) * (product.annual_production || 0)
              : undefined,

          ph_trsf_materials:
            (product.product_flow || footprintDoc.general_data?.activityType || "extraction") !== "extraction"
              ? (product.hydric_footprint_materials || materialsHydric) * (product.annual_production || 0)
              : undefined,
          ph_trsf_ecr:
            (product.product_flow || footprintDoc.general_data?.activityType || "extraction") !== "extraction"
              ? (product.hydric_footprint_electricity || electricityHydric + fuelsHydric + residueHydric) *
                (product.annual_production || 0)
              : undefined,
          ph_trsf_blue:
            (product.product_flow || footprintDoc.general_data?.activityType || "extraction") !== "extraction"
              ? productBlueWaterAllocation
              : undefined,
          ph_trsf_grey:
            (product.product_flow || footprintDoc.general_data?.activityType || "extraction") !== "extraction"
              ? productGrayWaterAllocation // Use product-specific allocation
              : undefined,
          ph_trsf:
            (product.product_flow || footprintDoc.general_data?.activityType || "extraction") !== "extraction"
              ? ((product.hydric_footprint_materials || materialsHydric) +
                  (product.hydric_footprint_electricity || electricityHydric + fuelsHydric + residueHydric) +
                  (product.hydric_footprint_water || waterHydric)) *
                (product.annual_production || 0)
              : undefined,

          // Total blue and grey water calculations (converted to annual totals)
          ph_blue: productBlueWaterAllocation,
          ph_grey: productGrayWaterAllocation, // Use product-specific allocation

          calculatedAt: footprintDoc.calculation_metadata?.last_calculated || footprintDoc.createdAt,
        };

        results.push(result);
      }
    });

    return results;
  }, [companyData]);

  // Create footprint summaries for the data table
  const footprintSummaries: FootprintSummary[] = useMemo(() => {
    if (companyData.footprints.length === 0) return [];

    const summaries: FootprintSummary[] = [];

    companyData.products.forEach((product) => {
      const footprintDoc = companyData.footprints.find((fp) => fp.id === product.footprint_id);

      if (footprintDoc && product.functional_unit) {
        summaries.push({
          id: product.id,
          product_name: product.name || "Unknown Product",
          product_type: product.product_type || "outro",
          stone_type_name: product.stone_type_name || t("results.hydric.notSpecified"),
          year: footprintDoc.year,
          functional_unit: product.functional_unit.quantity || 1,
          unit: product.functional_unit.unit || "m3",
          annual_production: product.annual_production || 0, // Added for linter compliance
          carbon_footprint: product.carbon_footprint_total || 0,
          hydric_footprint: product.hydric_footprint_total || 0,
          activity_type: footprintDoc.general_data?.activityType || "extraction",
          allocation_method: "economic", // Water footprint ALWAYS uses economic allocation
          companyId: footprintDoc.company_id,
        });
      }
    });

    return summaries;
  }, [companyData]);

  // Get unique values for filters
  const products = useMemo(() => {
    return Array.from(new Set(footprintSummaries.map((p) => p.product_name)));
  }, [footprintSummaries]);

  const years = useMemo(() => {
    if (!selectedProduct) {
      return [];
    }
    return Array.from(
      new Set(footprintSummaries.filter((fp) => fp.product_name === selectedProduct).map((fp) => fp.year.toString()))
    ).sort((a, b) => parseInt(b) - parseInt(a));
  }, [footprintSummaries, selectedProduct]);

  useEffect(() => {
    if (selectedProduct && selectedYear && years.length > 0) {
      const yearExists = years.includes(selectedYear);
      if (!yearExists) {
        onYearChange("");
      }
    }
  }, [selectedProduct, selectedYear, years, onYearChange]);

  // Get selected product details for dashboard
  const selectedProductData = useMemo(() => {
    if (!selectedProduct || !selectedYear) return null;

    return hydricResults.find(
      (result) => result.nome_produto === selectedProduct && result.year.toString() === selectedYear
    );
  }, [selectedProduct, selectedYear, hydricResults]);

  // Get the actual FootprintProduct for the selected product
  const selectedFootprintProduct = useMemo(() => {
    if (!selectedProduct || !selectedYear) return null;

    return companyData.products.find((p) => {
      const footprintDoc = companyData.footprints.find((fp) => fp.id === p.footprint_id);
      return p.name === selectedProduct && footprintDoc?.year.toString() === selectedYear;
    });
  }, [selectedProduct, selectedYear, companyData]);

  // Generate chart data based on activity type
  const chartData = useMemo((): HydricChartData[] => {
    if (!selectedProductData) return [];

    const { activityType } = selectedProductData;
    const data: HydricChartData[] = [];

    if (activityType === "extraction") {
      // RF 11.1 - Extraction only
      // Calculate the total of components being displayed for percentage calculation
      const totalDisplayedComponents =
        (selectedProductData.ph_ext_blue || 0) +
        (selectedProductData.ph_ext_grey || 0) +
        (selectedProductData.ph_ext_materials || 0) +
        (selectedProductData.ph_ext_ecr || 0);

      // Only show components if they have meaningful values and total is positive
      if (totalDisplayedComponents > 0) {
        if (selectedProductData.ph_ext_blue && selectedProductData.ph_ext_blue > 0) {
          data.push({
            name: t("results.hydric.extraction.blue"),
            value: selectedProductData.ph_ext_blue,
            percentage: (selectedProductData.ph_ext_blue / totalDisplayedComponents) * 100,
            color: "#0ea5e9", // sky-500
          });
        }
        if (selectedProductData.ph_ext_grey && selectedProductData.ph_ext_grey > 0) {
          data.push({
            name: t("results.hydric.extraction.grey"),
            value: selectedProductData.ph_ext_grey,
            percentage: (selectedProductData.ph_ext_grey / totalDisplayedComponents) * 100,
            color: "#64748b", // slate-500
          });
        }
        if (selectedProductData.ph_ext_materials && selectedProductData.ph_ext_materials > 0) {
          data.push({
            name: t("results.hydric.extraction.materials"),
            value: selectedProductData.ph_ext_materials,
            percentage: (selectedProductData.ph_ext_materials / totalDisplayedComponents) * 100,
            color: "#14b8a6", // teal-500
          });
        }
        if (selectedProductData.ph_ext_ecr && selectedProductData.ph_ext_ecr > 0) {
          data.push({
            name: t("results.hydric.extraction.energy"),
            value: selectedProductData.ph_ext_ecr,
            percentage: (selectedProductData.ph_ext_ecr / totalDisplayedComponents) * 100,
            color: "#f59e0b", // amber-500
          });
        }
      }
    } else if (activityType === "transformation") {
      // RF 11.2 - Transformation
      const totalDisplayedComponents =
        (selectedProductData.ph_ext || 0) + (selectedProductData.ph_trans || 0) + (selectedProductData.ph_trsf || 0);

      if (totalDisplayedComponents > 0) {
        if (selectedProductData.ph_ext && selectedProductData.ph_ext > 0) {
          data.push({
            name: t("results.hydric.phases.extraction"),
            value: selectedProductData.ph_ext,
            percentage: (selectedProductData.ph_ext / totalDisplayedComponents) * 100,
            color: "#14b8a6", // teal-500
          });
        }
        // Only show transport for transformation products, not extraction
        const productFlow = selectedFootprintProduct?.product_flow || "transformation";
        if (selectedProductData.ph_trans && selectedProductData.ph_trans > 0 && productFlow !== "extraction") {
          data.push({
            name: t("results.hydric.phases.transportation"),
            value: selectedProductData.ph_trans,
            percentage: (selectedProductData.ph_trans / totalDisplayedComponents) * 100,
            color: "#f59e0b", // amber-500
            breakdown: {
              ecr: selectedProductData.ph_trans, // Transportation is only energy
            },
          });
        }
        if (selectedProductData.ph_trsf && selectedProductData.ph_trsf > 0) {
          data.push({
            name: t("results.hydric.phases.transformation"),
            value: selectedProductData.ph_trsf,
            percentage: (selectedProductData.ph_trsf / totalDisplayedComponents) * 100,
            color: "#8b5cf6", // violet-500
            breakdown: {
              blue: selectedProductData.ph_trsf_blue,
              grey: selectedProductData.ph_trsf_grey,
              materials: selectedProductData.ph_trsf_materials,
              ecr: selectedProductData.ph_trsf_ecr,
            },
          });
        }
      }
    } else if (activityType === "extraction_and_transformation") {
      // RF 11.3 - Extraction and Transformation
      const totalDisplayedComponents =
        (selectedProductData.ph_ext || 0) + (selectedProductData.ph_trans || 0) + (selectedProductData.ph_trsf || 0);

      if (totalDisplayedComponents > 0) {
        if (selectedProductData.ph_ext && selectedProductData.ph_ext > 0) {
          data.push({
            name: t("results.hydric.phases.extraction"),
            value: selectedProductData.ph_ext,
            percentage: (selectedProductData.ph_ext / totalDisplayedComponents) * 100,
            color: "#14b8a6", // teal-500
            breakdown: {
              blue: selectedProductData.ph_ext_blue,
              grey: selectedProductData.ph_ext_grey,
              materials: selectedProductData.ph_ext_materials,
              ecr: selectedProductData.ph_ext_ecr,
            },
          });
        }
        // Only show transport for transformation products, not extraction
        const productFlow = selectedFootprintProduct?.product_flow || "transformation";
        if (selectedProductData.ph_trans && selectedProductData.ph_trans > 0 && productFlow !== "extraction") {
          data.push({
            name: t("results.hydric.phases.transportation"),
            value: selectedProductData.ph_trans,
            percentage: (selectedProductData.ph_trans / totalDisplayedComponents) * 100,
            color: "#f59e0b", // amber-500
            breakdown: {
              ecr: selectedProductData.ph_trans, // Transportation is only energy
            },
          });
        }
        if (selectedProductData.ph_trsf && selectedProductData.ph_trsf > 0) {
          data.push({
            name: t("results.hydric.phases.transformation"),
            value: selectedProductData.ph_trsf,
            percentage: (selectedProductData.ph_trsf / totalDisplayedComponents) * 100,
            color: "#8b5cf6", // violet-500
            breakdown: {
              blue: selectedProductData.ph_trsf_blue,
              grey: selectedProductData.ph_trsf_grey,
              materials: selectedProductData.ph_trsf_materials,
              ecr: selectedProductData.ph_trsf_ecr,
            },
          });
        }
      }
    }

    return data;
  }, [selectedProductData, t]);

  // Calculate direct and indirect footprints for extraction_and_transformation
  const getDirectAndIndirectFootprints = () => {
    if (!selectedProductData || selectedProductData.activityType !== "extraction_and_transformation") {
      return null;
    }

    const directFootprint =
      (selectedProductData.ph_ext_blue || 0) +
      (selectedProductData.ph_ext_grey || 0) +
      (selectedProductData.ph_trsf_blue || 0) +
      (selectedProductData.ph_trsf_grey || 0);

    const indirectFootprint =
      (selectedProductData.ph_ext_materials || 0) +
      (selectedProductData.ph_ext_ecr || 0) +
      (selectedProductData.ph_trans || 0) +
      (selectedProductData.ph_trsf_materials || 0) +
      (selectedProductData.ph_trsf_ecr || 0);

    return { directFootprint, indirectFootprint };
  };

  // Get legend content based on activity type
  const getLegendContent = () => {
    if (!selectedProductData) return null;

    const { activityType } = selectedProductData;
    const productFlow = selectedFootprintProduct?.product_flow;

    // When productFlow is available, use it as the primary determinant
    // This handles mixed companies where activityType might not be "extraction_and_transformation"
    const isExtractionProduct = productFlow === "extraction" ||
      (!productFlow && activityType === "extraction");

    const isTransformationProduct = productFlow === "transformation" ||
      (!productFlow && activityType === "transformation");

    // Show extraction legend for extraction products
    if (isExtractionProduct) {
      return (
        <div className="space-y-4">
          <div className="space-y-3">
            <p className="text-sm text-gray-700 font-medium">{t("results.hydric.legend.description")}</p>
            <div className="space-y-2 text-sm text-gray-600">
              <p>• {t("results.hydric.legend.extraction")}</p>
            </div>
          </div>

          <div className="space-y-3 pt-3 border-t">
            <div className="space-y-2 text-sm text-gray-600">
              <p>
                <strong>{t("results.hydric.legend.directBlue")}:</strong> {t("results.hydric.legend.directBlueDesc")}
              </p>
              <p>
                <strong>{t("results.hydric.legend.directGrey")}:</strong> {t("results.hydric.legend.directGreyDesc")}
              </p>
              <p>
                <strong>{t("results.hydric.legend.indirectEnergy")}:</strong>{" "}
                {t("results.hydric.legend.indirectEnergyDesc")}
              </p>
              <p>
                <strong>{t("results.hydric.legend.indirectMaterials")}:</strong>{" "}
                {t("results.hydric.legend.indirectMaterialsDesc")}
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Show transformation legend for transformation products
    if (isTransformationProduct) {
      return (
        <div className="space-y-4">
          <div className="space-y-3">
            <p className="text-sm text-gray-700 font-medium">
              {t("results.hydric.legend.transformation.description")}
            </p>
            <div className="space-y-2 text-sm text-gray-600">
              <p>• {t("results.hydric.legend.transformation.extraction")}</p>
              <p>• {t("results.hydric.legend.transformation.transportation")}</p>
              <p>• {t("results.hydric.legend.transformation.transformationPhase")}</p>
            </div>
          </div>

          <div className="space-y-3 pt-3 border-t">
            <div className="space-y-2 text-sm text-gray-600">
              <p>
                <strong>{t("results.hydric.legend.directBlue")}:</strong> {t("results.hydric.legend.directBlueDesc")}
              </p>
              <p>
                <strong>{t("results.hydric.legend.directGrey")}:</strong> {t("results.hydric.legend.directGreyDesc")}
              </p>
              <p>
                <strong>{t("results.hydric.legend.indirectEnergy")}:</strong>{" "}
                {t("results.hydric.legend.indirectEnergyDesc")}
              </p>
              <p>
                <strong>{t("results.hydric.legend.indirectMaterials")}:</strong>{" "}
                {t("results.hydric.legend.indirectMaterialsDesc")}
              </p>
            </div>
          </div>

          <div className="pt-3 border-t">
            <p className="text-sm text-gray-600">
              <strong>{t("results.hydric.legend.note")}</strong> {t("results.hydric.legend.transformation.materials")}
            </p>
          </div>
        </div>
      );
    }

    // Fallback for mixed companies when product_flow is not set or unknown
    // Default to extraction legend for mixed companies
    return (
      <div className="space-y-4">
        <div className="space-y-3">
          <p className="text-sm text-gray-700 font-medium">{t("results.hydric.legend.description")}</p>
          <div className="space-y-2 text-sm text-gray-600">
            <p>• {t("results.hydric.legend.extraction")}</p>
          </div>
        </div>

        <div className="space-y-3 pt-3 border-t">
          <div className="space-y-2 text-sm text-gray-600">
            <p>
              <strong>{t("results.hydric.legend.directBlue")}:</strong> {t("results.hydric.legend.directBlueDesc")}
            </p>
            <p>
              <strong>{t("results.hydric.legend.directGrey")}:</strong> {t("results.hydric.legend.directGreyDesc")}
            </p>
            <p>
              <strong>{t("results.hydric.legend.indirectEnergy")}:</strong>{" "}
              {t("results.hydric.legend.indirectEnergyDesc")}
            </p>
            <p>
              <strong>{t("results.hydric.legend.indirectMaterials")}:</strong>{" "}
              {t("results.hydric.legend.indirectMaterialsDesc")}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const directAndIndirect = getDirectAndIndirectFootprints();

  // Loading state
  const loading = footprintsLoading || productsLoading || consumptionsLoading;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Empty state when no company is found (only for non-shared views)
  if (!company && !isSharedView) {
    return (
      <div className="text-center py-16">
        <Activity className="h-16 w-16 mx-auto mb-4 text-gray-300" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">{t("results.empty.noCompany.title")}</h3>
        <p className="text-gray-500 max-w-md mx-auto">{t("results.empty.noCompany.description")}</p>
      </div>
    );
  }

  // Empty state when no footprint data exists for the company
  if (companyData.footprints.length === 0 && companyData.products.length === 0) {
    return (
      <div className="space-y-6">
        {/* Empty State */}
        <Card>
          <CardContent className="text-center py-16">
            <Droplets className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t("results.hydric.empty.title")}</h3>
            <p className="text-gray-500 max-w-md mx-auto mb-6">{t("results.hydric.empty.description")}</p>
            <div className="bg-gray-50 rounded-lg p-4 max-w-md mx-auto">
              <h4 className="font-medium text-gray-900 mb-2">{t("results.hydric.empty.getStarted")}</h4>
              <ul className="text-sm text-gray-600 space-y-1 text-left">
                <li>• {t("results.hydric.empty.step1")}</li>
                <li>• {t("results.hydric.empty.step2")}</li>
                <li>• {t("results.hydric.empty.step3")}</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Chart and Analysis Section */}
      {footprintSummaries.length > 0 ? (
        <>
          {!isSharedView && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("results.hydric.filters.product")}</label>
                <Select value={selectedProduct} onValueChange={onProductChange}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("results.hydric.filters.selectProduct")} />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((product) => (
                      <SelectItem key={product} value={product}>
                        {product}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">{t("results.hydric.filters.year")}</label>
                <Select value={selectedYear} onValueChange={onYearChange}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("results.hydric.filters.selectYear")} />
                  </SelectTrigger>
                  <SelectContent>
                    {years.length > 0 ? (
                      years.map((year) => (
                        <SelectItem key={year} value={year}>
                          {year}
                        </SelectItem>
                      ))
                    ) : (
                      <div className="px-2 py-1.5 text-sm text-gray-500">{t("results.hydric.noYearAvailable")}</div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Chart Analysis Dashboard */}
          {selectedProduct && selectedYear ? (
            selectedProductData ? (
              <div className="space-y-6">
                {/* Total Footprint */}
                <Card data-pdf-section="hydric-total" className="bg-cyan-50 border-cyan-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3">
                      <Droplets className="h-8 w-8 text-cyan-600" />
                      <div>
                        <h3 className="text-lg font-semibold text-cyan-800">
                          {t("results.hydric.dashboard.totalFootprint")}
                        </h3>
                        <p className="text-3xl font-bold text-cyan-700">
                          {formatNumber(selectedProductData.ph_prod)} {t("results.hydric.units.m3")}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>


                {/* Direct and Indirect Footprints for extraction_and_transformation */}
                {directAndIndirect && (
                  <Card data-pdf-section="hydric-direct-indirect" className="bg-green-50 border-green-200">
                    <CardHeader>
                      <CardTitle className="text-lg text-green-800">
                        {t("results.hydric.dashboard.directIndirect")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="text-center">
                          <p className="text-sm text-gray-600 mb-1">{t("results.hydric.dashboard.directFootprint")}</p>
                          <p className="text-xl font-bold text-green-700">
                            {formatNumber(directAndIndirect.directFootprint)} m³
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-gray-600 mb-1">
                            {t("results.hydric.dashboard.indirectFootprint")}
                          </p>
                          <p className="text-xl font-bold text-green-700">
                            {formatNumber(directAndIndirect.indirectFootprint)} m³
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Chart and Legend */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div data-pdf-section="hydric-pie-chart">
                    <HydricPieChart data={chartData} title={t("results.hydric.dashboard.chart")} />
                  </div>

                  <Card data-pdf-section="hydric-legend">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Info className="h-5 w-5" />
                        {t("results.hydric.legend.title")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>{getLegendContent()}</CardContent>
                  </Card>
                </div>

                {/* Enhanced Analysis Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Material Breakdown Chart */}
                  {selectedFootprintProduct && (
                    <div data-pdf-section="hydric-material-breakdown">
                      <MaterialBreakdownChart
                        product={selectedFootprintProduct}
                        consumptions={companyData.consumptions}
                        type="hydric"
                      />
                    </div>
                  )}

                  {/* Allocation Analysis */}
                  <div data-pdf-section="hydric-allocation">
                    <AllocationAnalysisCard data={selectedProductData} />
                  </div>
                </div>

                {/* Transport legs breakdown (transformation products) */}
                {selectedFootprintProduct?.transports && selectedFootprintProduct.transports.length > 0 && (
                  <TransportLegsCard transports={selectedFootprintProduct.transports} />
                )}
              </div>
            ) : (
              <div className="text-center py-16">
                <Droplets className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">{t("results.hydric.noDataFound.title")}</h3>
                <p className="text-gray-500 max-w-md mx-auto mb-4">
                  {t("results.hydric.noDataFound.description", {
                    product: selectedProduct,
                    year: selectedYear
                  })}
                </p>
                <p className="text-sm text-gray-400">
                  {t("results.hydric.noDataFound.checkCalculations")}
                </p>
              </div>
            )
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Droplets className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>
                {t("results.hydric.filters.selectProduct")} {t("common.and").toLowerCase()}{" "}
                {t("results.hydric.filters.selectYear").toLowerCase()}
              </p>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12">
          <Droplets className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">{t("results.hydric.dashboard.empty.title")}</h3>
          <p className="text-gray-500">{t("results.hydric.dashboard.empty.description")}</p>
        </div>
      )}
    </div>
  );
};
